<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Reporting webpage -->
<template>
  <div class="page reporting-page">
    <div class="page-heading">
      <h1>Reporting</h1>
      <av-button @click="download"><i class="fal fa-cloud-download"></i> Download</av-button>
    </div>

    <div class="page-content">
      <div class="sidebar">
        <div class="sidebar-header">
          Filters
        </div>
        <div class="sidebar-content">
          <!-- Date Filter -->
          <div class="filter-section time-range">
            <h6>
              Date
              <!-- <i class="fal fa-chevron-up"></i> -->
            </h6>
            <div class="padding">
              <av-radio-group v-model="dateRangeType">
                <av-radio-button value="range">Date Range</av-radio-button>
                <av-radio-button value="past">Before Date</av-radio-button>
                <av-radio-button value="future">After Date</av-radio-button>
              </av-radio-group>
              <div class="date-range-inputs">
                <div :class="{ hidden: dateRangeType === 'past', full: dateRangeType !== 'range'}"><input class="start-time av-input"/></div>
                <div :class="{ hidden: dateRangeType === 'future', full: dateRangeType !== 'range'}"><input class="end-time av-input" /></div>
              </div>
            </div>
          </div>

          <!-- Reviewer Filter -->
          <div class="filter-section" :class="{ closed: false }">
            <h6>
              Reviewers
              <!-- <i class="fal fa-chevron-up"></i> -->
            </h6>
            <av-checkbox-group v-if="reviewers" v-model="reportSpec.reviewerIds">
              <ul>
                <li v-for="reviewer of reviewers" :key="reviewer.id">
                  <span>{{ reviewer.firstName }} {{ reviewer.lastName }}</span>
                  <av-checkbox :value="reviewer.id"></av-checkbox>
                </li>
              </ul>
            </av-checkbox-group>
          </div>

          <!-- Appointment Type Filter -->
          <div class="filter-section" :class="{ closed: false }">
            <h6>
              Appointment Type
              <!-- <i class="fal fa-chevron-up"></i> -->
            </h6>
            <av-checkbox-group v-if="appointmentTypes" v-model="reportSpec.appointmentTypeIds">
              <ul>
                <li v-for="appointmentType of appointmentTypes" :key="appointmentType.id">
                  <span>{{ appointmentType.name }}</span>
                  <av-checkbox :value="appointmentType.id"></av-checkbox>
                </li>
              </ul>
            </av-checkbox-group>
          </div>

          <!-- Sponsor Filter -->
          <div class="filter-section">
            <h6>Sponsors</h6>
            <div class="padding">
              <div class="sponsor-search-screen" v-if="sponsorSearchKey !== ''" @click="sponsorSearchKey = ''"></div>
              <div
                class="sponsor-search-results"
                v-if="sponsorSearchKey !== ''"
                :style="{ bottom: sponsorSearchPos.bottom + 'px', left: sponsorSearchPos.left + 'px' }"
              >
                <ul>
                  <li v-for="sponsor of filteredSponsors" :key="sponsor.id" @click="addSponsor(sponsor.id)"><span>{{ sponsor.name }}</span></li>
                </ul>
              </div>

              <input class="sponsor-search" v-model="sponsorSearchKey" placeholder="Search For Sponsor" />
            </div>
            <ul class="selected-sponsors">
              <li v-for="sponsor of selectedSponsors" :key="sponsor.id">
                <span>{{ sponsor.name }}</span>
                <i class="fal fa-times-circle" @click="removeSponsor(sponsor.id)"></i>
              </li>
              <li class="no-sponsors" v-if="selectedSponsors.length === 0">
                <span>No Sponsor Filters</span>
              </li>
            </ul>
          </div>

          <!-- Status -->
          <div class="filter-section">
            <h6>Status</h6>
            <div class="padding">
              <av-checkbox v-model="reportSpec.includeCancelled">Include Cancelled</av-checkbox><br><br>
              <av-checkbox v-model="reportSpec.includeRescheduled">Include Rescheduled</av-checkbox>
            </div>
          </div>
        </div>
      </div>

      <div class="results" v-if="results">
        <div class="results-header">
          <div class="count">
            {{ results.appointments.length }} Appointments
          </div>
          <div class="tabs">
            <a @click="resultsView = 'appointments'" :class="{active: resultsView === 'appointments'}">Appointments</a>
            <a @click="resultsView = 'reviewer'" :class="{active: resultsView === 'reviewer'}">Reviewer Count</a>
            <a @click="resultsView = 'appointmentType'" :class="{active: resultsView === 'appointmentType'}">Appointment Type Count</a>
            <a @click="resultsView = 'sponsor'" :class="{active: resultsView === 'sponsor'}">Sponsor Count</a>
          </div>
        </div>

        <div class="table-wrap">
          <!-- Appointments -->
          <table v-if="resultsView === 'appointments'">
            <tr>
              <th>Start</th>
              <th>End</th>
              <th>Review Type</th>
              <th>Proposal Number Type</th>
              <th>Proposal Record Number</th>
              <th>PI First Name</th>
              <th>PI Last Name</th>
              <th>PI Email</th>
              <th>PI Phone</th>
              <th>Additional Contacts</th>
              <th>Sponsor</th>
              <th>Reviewer First Name</th>
              <th>Reviewer Last Name</th>
              <th>Review Deadline</th>
              <th>Cancelled</th>
              <th>Rescheduled</th>
              <th>ScheduledOn</th>
              <th v-for="dc of results.dynamicColumns" :key="dc">{{ dc }}</th>
            </tr>
            <tr v-for="result of results.appointments" :key="result.ProposalNumber">
              <td class="start">{{ formatTime(result.Start, 'MMM do yyyy h:mm:aa') }}</td>
              <td class="end">{{ formatTime(result.End, 'MMM do yyyy h:mm:aa') }}</td>
              <td class="appointment-type">{{ result.AppointmentType }}</td>
              <td>{{ result.ProposalNumberType }}</td>
              <td>{{ result.ProposalNumber }}</td>
              <td>{{ result.PIFirstName }}</td>
              <td>{{ result.PILastName }}</td>
              <td>{{ result.PIEmail }}</td>
              <td>{{ result.PIPhone }}</td>
              <td v-html="result.AdditionalContacts"></td>
              <td>{{ result.Sponsor }}</td>
              <td>{{ result.ReviewerFirstName }}</td>
              <td>{{ result.ReviewerLastName }}</td>
              <td>{{ formatTime(result.ReviewDeadline, 'MMM do yyyy h:mm:aa')}}</td>
              <td>{{ result.Cancelled }}</td>
              <td>{{ result.Rescheduled }}</td>
              <td>{{ formatTime(result.ScheduledOn, 'MMM do yyyy h:mm:aa') }}</td>
              <td v-for="dc of results.dynamicColumns" :key="dc">{{ result[dc] }}</td>
            </tr>
          </table>

          <!-- Count Table -->
          <table v-if="resultsView !== 'appointments'">
            <tr>
              <th v-if="resultsView === 'reviewer'">Reviewer</th>
              <th v-if="resultsView === 'sponsor'">Sponsor</th>
              <th v-if="resultsView === 'appointmentType'">Appointment Type</th>
              <th>Appointments</th>
            </tr>
            <tr v-for="count of selectedCounts" :key="count.name">
              <td>{{ count.name }}</td>
              <td>{{ count.count }}</td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { AxiosResponse } from 'axios'
import { endOfDay, endOfWeek, format, startOfDay, startOfWeek } from 'date-fns'
import Litepicker from 'litepicker'
import { defineComponent } from 'vue'
import AppointmentTypeService from '../common/appointmentTypeService'
import ReportingService from '../common/reportingService'
import SponsorService from '../common/sponsorService'
import UserService from '../common/userService'
import Appointment from '../models/appointment'
import { AppointmentType } from '../models/appointmentType'
import { ReportAppointment, ReportCount, ReportResult } from '../models/reportResult'
import { Sponsor } from '../models/sponsor'
import User from '../models/user'

type DateRangeType = 'range' | 'past' | 'future'

export default defineComponent({
  name: 'Reporting',

  data () {
    return {
      dateRangeType: 'range' as DateRangeType,
      /** report specification form */
      reportSpec: {
        start: startOfWeek(new Date()) as Date | null,
        end: endOfWeek(new Date()) as Date | null,
        sponsorIds: [] as Array<number>,
        appointmentTypeIds: [] as Array<number>,
        reviewerIds: [] as Array<number>,
        includeCancelled: true,
        includeRescheduled: true
      },
      results: null as unknown as ReportResult,
      countByReviewers: [],
      countByAppointmentType: [],
      countBySponsor: [],
      reportUrl: '',
      reviewers: null as unknown as Array<User>,
      sponsors: [] as Array<Sponsor>,
      appointmentTypes: null as unknown as Array<AppointmentType>,
      startPicker: null as unknown as Litepicker,
      endPicker: null as unknown as Litepicker,
      sponsorSearchKey: '',
      sponsorSearchPos: { bottom: 0, left: 0 },
      showSponsorSelection: false,
      resultsView: 'appointments'
    }
  },

  computed: {
    /** list of filtered sponsors */
    filteredSponsors (): Array<Sponsor> {
      if (this.sponsorSearchKey !== '') {
        return this.sponsors.filter(s => RegExp(this.sponsorSearchKey, 'i').test(s.name))
      } else {
        return this.sponsors
      }
    },

    /** list of selected sponsors */
    selectedSponsors (): Array<Sponsor> {
      return this.sponsors.filter(s => this.reportSpec.sponsorIds.findIndex(id => s.id === id) !== -1)
    },

    /** list of report counts */
    selectedCounts (): Array<ReportCount> {
      if (this.resultsView === 'reviewer') {
        return this.results.reviewerCounts
      } else if (this.resultsView === 'sponsor') {
        return this.results.sponsorCounts
      } else {
        return this.results.appointmentTypeCounts
      }
    }
  },

  watch: {
    /**
     * Sets date range types; range, past, future
     */
    dateRangeType () {
      if (this.dateRangeType === 'range') {
        this.reportSpec.start = startOfWeek(new Date())
        this.reportSpec.end = endOfWeek(new Date())
      } else if (this.dateRangeType === 'past') {
        this.reportSpec.start = null
        this.reportSpec.end = endOfDay(new Date())
      } else if (this.dateRangeType === 'future') {
        this.reportSpec.start = startOfDay(new Date())
        this.reportSpec.end = null
      }

      if (this.reportSpec.start) {
        console.log('s')
        this.startPicker.setDate(this.reportSpec.start)
      }

      if (this.reportSpec.end) {
        console.log('e')
        this.endPicker.setDate(this.reportSpec.end)
      }
    },

    /**
     * Sponsor search
     */
    sponsorSearchKey () {
      const searchbar = document.querySelector('.sponsor-search')?.getBoundingClientRect()
      if (searchbar) {
        this.sponsorSearchPos.left = 10
        this.sponsorSearchPos.bottom = window.innerHeight - searchbar.top + 10
      }
    },

    reportSpec: {
      // genereate report
      handler () {
        this.generateReport()
      },
      deep: true,
      immediate: true
    }
  },

  mounted () {
    // load appointment types
    AppointmentTypeService.getAll().then(res => {
      this.appointmentTypes = res.data
      this.appointmentTypes.forEach(at => this.reportSpec.appointmentTypeIds.push(at.id))
    })

    // load sponsors
    SponsorService.getAll().then(res => {
      this.sponsors = res.data
    })

    // load OSP staffers that are reviewers
    UserService.getOspStaff().then(res => {
      this.reviewers = res.data.filter(u => u.permissions.findIndex(p => p.code === 'REVU') !== -1)
      this.reviewers.forEach(r => this.reportSpec.reviewerIds.push(r.id))
    })

    this.setupTimePickers()
  },

  methods: {
    /**
     * add sponsor to report spec
     */
    addSponsor (sponsorId: number) {
      this.reportSpec.sponsorIds.push(sponsorId)
      this.sponsorSearchKey = ''
    },

    /**
     * download datafrom report spec
     */
    download () {
      ReportingService.downloadReport(this.reportSpec).then(res => {
        this.reportUrl = res.data
        window.location = this.reportUrl as any
      })
    },

    /**
     * Formats the given date in the specified format
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    /**
     * remove sponsor from report spec
     */
    removeSponsor (sponsorId: number) {
      const ind = this.reportSpec.sponsorIds.findIndex(x => x === sponsorId)
      this.reportSpec.sponsorIds.splice(ind, 1)
    },

    /**
     * sets start and end time from picker calendar
     */
    setupTimePickers () {
      this.startPicker = new Litepicker({ element: document.querySelector('.start-time'), singleMode: true, onSelect: (date) => { console.log('setting start'); this.reportSpec.start = date } })
      this.startPicker.setDate(this.reportSpec.start)

      this.endPicker = new Litepicker({ element: document.querySelector('.end-time'), singleMode: true, onSelect: (date) => { console.log('setting end'); this.reportSpec.end = date } })
      this.endPicker.setDate(this.reportSpec.end)
    },

    /**
     * generate report from spec
     */
    generateReport () {
      ReportingService.generateReport(this.reportSpec).then((res: AxiosResponse<ReportResult>) => {
        res.data.appointments.forEach((r: ReportAppointment) => {
          r.Start = new Date(r.Start)
          r.End = new Date(r.End)
          r.ScheduledOn = new Date(r.ScheduledOn)
          r.ReviewDeadline = new Date(r.ReviewDeadline)
        })
        this.results = res.data
      })
    }
  }
})
</script>
